import {useCallback, useEffect, useMemo, useState} from "react";
import {ColumnOrderState, SortingState, Updater} from "@tanstack/react-table";
import {columns} from "@/features/types/AutoTableInterface";
import {defaultPresetColumnsList, PresetValue, staticColumns} from "@/features/types/AutoTablePresetInterface";
import {
    deleteAutoTablePresetFilter,
    getAllAutoTablePresetFilters,
    saveAutoTablePresetFilter,
    setDefaultAutoTablePresetFilter
} from "@/features/controllers/AutoTableController";
import {Filters} from "@/features/types/AutoTableFilterInterface";

export function useTablePreset(initial?: Partial<PresetValue>) {
    const [preset, setPreset] = useState<PresetValue>(() => {
        if (initial) return initial as PresetValue;

        const allColumns: Record<string, { visibility: boolean; sizing: number; order: number }> = {};

        Object.entries(staticColumns)
            .sort(([, a], [, b]) => a.order - b.order)
            .forEach(([key, v]) => {
                allColumns[key] = {visibility: v.visibility, sizing: v.size, order: v.order};
            });

        let nextOrder = Object.keys(allColumns).length;
        defaultPresetColumnsList.forEach((key) => {
            allColumns[key] = {
                visibility: true,
                sizing: columns.find(c => c.accessorKey === key)?.size ?? 120,
                order: nextOrder++
            };
        });

        columns
            .map(c => c.accessorKey)
            .filter(key => !(key in allColumns))
            .forEach(key => {
                allColumns[key] = {
                    visibility: false,
                    sizing: columns.find(c => c.accessorKey === key)?.size ?? 120,
                    order: nextOrder++
                };
            });

        return {
            filters: {},
            sorting: [],
            config: {
                isDefault: false,
                columns: allColumns
            }
        } as PresetValue;
    });

    const [presetsLoaded, setPresetsLoaded] = useState(false);

    const columnVisibility = useMemo(
        () => Object.fromEntries(
            Object.entries(preset.config.columns).map(([key, col]) => [key, col.visibility])
        ),
        [preset]
    );

    const columnOrder = useMemo(
        () => [
            ...Object.entries(staticColumns)
                .sort(([, a], [, b]) => a.order - b.order)
                .map(([key]) => key),
            ...Object.entries(preset.config.columns)
                .filter(([key]) => !(key in staticColumns))
                .sort(([, a], [, b]) => a.order - b.order)
                .map(([key]) => key),
        ],
        [preset]
    );

    const columnSizing = useMemo(
        () => Object.fromEntries(
            Object.entries(preset.config.columns).map(([key, col]) => [key, col.sizing])
        ),
        [preset]
    );

    const loadAllPresetsFromServer = useCallback(() => getAllAutoTablePresetFilters(), []);

    const savePresetToServer = useCallback(async (presetName: string) => {
        await saveAutoTablePresetFilter(presetName, preset);
    }, [preset]);

    const deletePresetOnServer = useCallback(async (presetName: string) => {
        await deleteAutoTablePresetFilter(presetName);
    }, []);

    const setDefaultPresetOnServer = useCallback(async (presetName: string) => {
        await setDefaultAutoTablePresetFilter(presetName);
    }, []);

    // Auto-ładowanie domyślnego presetu przy starcie
    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            try {
                const data = await getAllAutoTablePresetFilters();
                if (cancelled) return;

                if (data.default && data.default in data.presets) {
                    // Ustaw domyślny preset jako aktualny
                    setPreset(prev => ({
                        ...prev,
                        ...data.presets[data.default as string],
                    }));
                }
            } catch (e) {
                console.error("Błąd przy auto-table-preset-get:", e);
            } finally {
                if (!cancelled) setPresetsLoaded(true);
            }
        };

        void init();

        return () => {
            cancelled = true;
        };
    }, []);

    const handleColumnVisibilityChange = useCallback((updater: Updater<Record<string, boolean>>) => {
        setPreset(prev => {
            const newColumns = {...prev.config.columns};
            const newVisibility = typeof updater === "function"
                ? updater(Object.fromEntries(Object.entries(newColumns).map(([k, v]) => [k, v.visibility])))
                : updater;
            Object.entries(newVisibility).forEach(([k, v]) => {
                if (newColumns[k]) newColumns[k].visibility = v;
            });
            return {...prev, config: {...prev.config, columns: newColumns}};
        });
    }, []);

    const handleColumnSizingChange = useCallback((updater: Updater<Record<string, number>>) => {
        setPreset(prev => {
            const newColumns = {...prev.config.columns};
            const newSizing = typeof updater === "function"
                ? updater(Object.fromEntries(Object.entries(newColumns).map(([k, v]) => [k, v.sizing])))
                : updater;
            Object.entries(newSizing).forEach(([k, v]) => {
                if (newColumns[k]) newColumns[k].sizing = v;
            });
            return {...prev, config: {...prev.config, columns: newColumns}};
        });
    }, []);

    const handleColumnOrderChange = useCallback((updater: ColumnOrderState | ((old: ColumnOrderState) => ColumnOrderState)) => {
        setPreset(prev => {
            const oldOrder = Object.entries(prev.config.columns)
                .sort((a, b) => a[1].order - b[1].order)
                .map(([k]) => k);

            const newOrder = typeof updater === "function" ? updater(oldOrder) : updater;
            const newColumns = {...prev.config.columns};
            newOrder.forEach((key, idx) => {
                if (newColumns[key]) newColumns[key].order = idx;
            });
            return {...prev, config: {...prev.config, columns: newColumns}};
        });
    }, []);

    // --- filters & sorting ---
    const setFilters = useCallback((filters: Filters) => {
        setPreset(prev => ({...prev, filters}));
    }, []);

    const setSorting = useCallback((updater: SortingState | ((old: SortingState) => SortingState)) => {
        setPreset(prev => ({
            ...prev,
            sorting: typeof updater === "function" ? updater(prev.sorting) : updater,
        }));
    }, []);

    const loadPreset = useCallback((p: Partial<PresetValue>) => {
        setPreset(prev => ({
            ...prev,
            ...p,
            config: p.config ?? prev.config,
            filters: p.filters ?? prev.filters,
            sorting: p.sorting ?? prev.sorting,
        }));
    }, []);

    return {
        preset,
        presetsLoaded,
        columnVisibility,
        columnSizing,
        columnOrder,

        setPreset,
        loadPreset,
        handleColumnVisibilityChange,
        handleColumnSizingChange,
        handleColumnOrderChange,
        setFilters,
        setSorting,

        loadAllPresetsFromServer,
        savePresetToServer,
        deletePresetOnServer,
        setDefaultPresetOnServer,
    };
}

export type ReturnTypeUseTablePreset = ReturnType<typeof useTablePreset>;
